import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Droplets,
  Factory,
  Sprout,
  FlaskConical,
  Globe,
  Landmark,
  Building2,
  Handshake,
  Users,
  Target,
  Truck,
  Printer
} from "lucide-react";
import { LOGO_URL } from "../data/content";

const programContent = {
  fr: {
    back: "Retour à l'accueil",
    hero: {
      label: "PROGRAMME PHARE",
      title: "Résilience Hydrique Sénégal 2035",
      subtitle: "Programme national de résilience hydrique et d'industrialisation des sols agricoles",
      description: "Initié et structuré par Skymoon Consultancy et Civentia, ce programme d'envergure vise la création au Sénégal d'une plateforme industrielle de production, de formulation et de diffusion de solutions de rétention d'eau — avec l'ambition de faire du pays le premier hub africain des technologies de résilience hydrique."
    },
    context: {
      title: "Transformer une Vulnérabilité en Opportunité",
      text: "Le Sahel fait face à une contrainte structurelle : la faible disponibilité de l'eau pour l'agriculture. Plutôt que de la subir, ce programme propose de la transformer en opportunité industrielle, agricole et régionale. En valorisant chaque litre d'eau, en produisant localement et en créant des référentiels africains, le Sénégal peut sécuriser ses rendements, réduire sa vulnérabilité climatique et devenir exportateur de solutions adaptées aux réalités sahéliennes.",
      pillars: [
        { icon: "Factory", title: "Plateforme Industrielle", desc: "Production, formulation et conditionnement locaux de solutions de rétention d'eau, adossés à une plateforme logistique nationale et régionale." },
        { icon: "Sprout", title: "Souveraineté Agricole", desc: "Sécurisation des rendements, réduction de la vulnérabilité climatique des sols sahéliens et meilleure valorisation de chaque litre d'eau." },
        { icon: "FlaskConical", title: "R&D & Formation", desc: "Laboratoire de contrôle, centre de recherche agronomique et montée en compétences locales pour créer des référentiels africains." },
        { icon: "Globe", title: "Rayonnement Régional", desc: "Approvisionnement de l'Afrique de l'Ouest et exportation de solutions créatrices d'emplois et de valeur ajoutée." }
      ]
    },
    phases: {
      label: "Feuille de Route",
      title: "Cinq Phases vers 2035",
      items: [
        { period: "2026 — 2027", title: "Décision & Structuration", desc: "Sélection du partenaire technologique, structuration du financement et mise en place du cadre institutionnel et partenarial." },
        { period: "2028 — 2029", title: "Construction & Mise en Service", desc: "Édification de la plateforme industrielle, du laboratoire et du centre de formation. Démarrage de la production locale." },
        { period: "2030 — 2031", title: "Montée en Puissance Nationale", desc: "Déploiement auprès des producteurs, stabilisation opérationnelle et consolidation des référentiels agronomiques." },
        { period: "2032 — 2033", title: "Expansion Régionale", desc: "Approvisionnement des marchés d'Afrique de l'Ouest et structuration de la plateforme logistique régionale." },
        { period: "2034 — 2035", title: "Diversification & Leadership Africain", desc: "Positionnement du Sénégal comme hub africain des technologies de résilience hydrique et élargissement des gammes de solutions." }
      ]
    },
    governance: {
      label: "Gouvernance",
      title: "Une Architecture Claire des Responsabilités",
      principle: "« L'État oriente et facilite ; la société de projet exploite ; le partenaire transfère ; la recherche valide ; les producteurs déploient. »",
      actors: [
        { icon: "Landmark", name: "État & Institutions", role: "Impulsion, cadre réglementaire et facilitation du programme." },
        { icon: "Handshake", name: "Skymoon Consultancy", role: "Initiation du programme, mobilisation des partenaires internationaux, structuration des financements et accès aux marchés régionaux." },
        { icon: "Sprout", name: "Civentia", role: "Ancrage national, innovation et contenu local, en lien avec les institutions, la recherche et les producteurs." },
        { icon: "Building2", name: "Société de Projet", role: "Portage des actifs, production, commercialisation et gestion opérationnelle." },
        { icon: "Target", name: "Partenaire Technologique", role: "Apport du procédé, transfert de technologie et garanties de performance. Sélection en cours." },
        { icon: "Users", name: "Investisseurs", role: "Apport en capital et discipline financière du programme." },
        { icon: "FlaskConical", name: "Recherche", role: "Validation scientifique indépendante des solutions déployées." },
        { icon: "Truck", name: "Producteurs & Distributeurs", role: "Adoption, application sur le terrain et remontée des données agronomiques." }
      ]
    },
    impact: {
      label: "Impact Attendu",
      title: "Un Programme au Service de la Souveraineté",
      items: [
        "Augmentation de la disponibilité d'eau utile dans les sols agricoles",
        "Sécurisation des rendements et réduction de la vulnérabilité climatique",
        "Création d'emplois qualifiés et montée en compétences locales",
        "Production locale et réduction de la dépendance aux importations",
        "Création de référentiels agronomiques africains",
        "Positionnement du Sénégal comme exportateur régional de solutions"
      ]
    },
    cta: {
      title: "Investisseurs & Partenaires Institutionnels",
      text: "Vous souhaitez en savoir plus sur le programme ou explorer une participation ? Contactez-nous.",
      button: "Nous Contacter"
    }
  },
  en: {
    back: "Back to home",
    hero: {
      label: "FLAGSHIP PROGRAM",
      title: "Senegal Water Resilience 2035",
      subtitle: "National program for water resilience and agricultural soil industrialization",
      description: "Initiated and structured by Skymoon Consultancy and Civentia, this large-scale program aims to create in Senegal an industrial platform for the production, formulation and distribution of water retention solutions — with the ambition of making the country the first African hub for water resilience technologies."
    },
    context: {
      title: "Turning a Vulnerability into an Opportunity",
      text: "The Sahel faces a structural constraint: low water availability for agriculture. Rather than enduring it, this program proposes to turn it into an industrial, agricultural and regional opportunity. By making every liter of water count, producing locally and creating African standards, Senegal can secure its yields, reduce its climate vulnerability and become an exporter of solutions adapted to Sahelian realities.",
      pillars: [
        { icon: "Factory", title: "Industrial Platform", desc: "Local production, formulation and packaging of water retention solutions, backed by a national and regional logistics platform." },
        { icon: "Sprout", title: "Agricultural Sovereignty", desc: "Securing yields, reducing the climate vulnerability of Sahelian soils and making every liter of water count." },
        { icon: "FlaskConical", title: "R&D & Training", desc: "Quality control laboratory, agronomic research center and local skills development to create African standards." },
        { icon: "Globe", title: "Regional Reach", desc: "Supplying West Africa and exporting solutions that generate jobs and added value." }
      ]
    },
    phases: {
      label: "Roadmap",
      title: "Five Phases Towards 2035",
      items: [
        { period: "2026 — 2027", title: "Decision & Structuring", desc: "Technology partner selection, financing structuring and setup of the institutional and partnership framework." },
        { period: "2028 — 2029", title: "Construction & Commissioning", desc: "Building the industrial platform, laboratory and training center. Start of local production." },
        { period: "2030 — 2031", title: "National Scale-Up", desc: "Deployment with producers, operational stabilization and consolidation of agronomic standards." },
        { period: "2032 — 2033", title: "Regional Expansion", desc: "Supplying West African markets and structuring the regional logistics platform." },
        { period: "2034 — 2035", title: "Diversification & African Leadership", desc: "Positioning Senegal as the African hub for water resilience technologies and expanding solution ranges." }
      ]
    },
    governance: {
      label: "Governance",
      title: "A Clear Architecture of Responsibilities",
      principle: "\u201CThe State guides and facilitates; the project company operates; the partner transfers; research validates; producers deploy.\u201D",
      actors: [
        { icon: "Landmark", name: "State & Institutions", role: "Impetus, regulatory framework and program facilitation." },
        { icon: "Handshake", name: "Skymoon Consultancy", role: "Program initiation, international partner mobilization, financing structuring and access to regional markets." },
        { icon: "Sprout", name: "Civentia", role: "National anchoring, innovation and local content, working with institutions, research and producers." },
        { icon: "Building2", name: "Project Company", role: "Asset ownership, production, sales and operational management." },
        { icon: "Target", name: "Technology Partner", role: "Process contribution, technology transfer and performance guarantees. Selection in progress." },
        { icon: "Users", name: "Investors", role: "Capital contribution and financial discipline of the program." },
        { icon: "FlaskConical", name: "Research", role: "Independent scientific validation of deployed solutions." },
        { icon: "Truck", name: "Producers & Distributors", role: "Adoption, field application and agronomic data feedback." }
      ]
    },
    impact: {
      label: "Expected Impact",
      title: "A Program Serving Sovereignty",
      items: [
        "Increased useful water availability in agricultural soils",
        "Secured yields and reduced climate vulnerability",
        "Skilled job creation and local skills development",
        "Local production and reduced import dependency",
        "Creation of African agronomic standards",
        "Positioning Senegal as a regional exporter of solutions"
      ]
    },
    cta: {
      title: "Investors & Institutional Partners",
      text: "Want to learn more about the program or explore a participation? Get in touch.",
      button: "Contact Us"
    }
  }
};

const iconMap = { Factory, Sprout, FlaskConical, Globe, Landmark, Building2, Handshake, Users, Target, Truck };

function PrintHeader() {
  return (
      <div className="hidden print:flex items-center justify-between pb-6 mb-2 border-b border-gray-300">
        <img src={LOGO_URL} alt="Skymoon Consultancy" className="h-14 w-auto" />
        <div className="text-right">
          <p className="font-serif font-bold text-lg">Skymoon Consultancy</p>
          <p className="text-sm">Dubai · Dakar — contact@skymoonconsultancy.com</p>
        </div>
      </div>
  );
}

function ProgramNav({ t, lang, setLang }) {
  const navigate = useNavigate();
  return (
      <nav className="nav-fixed nav-scrolled print:hidden" data-testid="program-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="program-logo-link">
              <img src={LOGO_URL} alt="Skymoon" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.print()}
                className="hidden sm:flex items-center gap-2 text-sm text-[#C5A059] border border-[#C5A059]/40 rounded-full px-4 py-2 hover:bg-[#C5A059]/10 transition-colors"
                data-testid="program-print-btn"
              >
                <Printer size={15} />
                {lang === "fr" ? "Exporter en PDF" : "Export as PDF"}
              </button>
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#C5A059] transition-colors"
                data-testid="program-back-home"
              >
                <ArrowLeft size={16} />
                {t.back}
              </button>
              <div className="lang-toggle">
                <button onClick={() => setLang("fr")} className={`lang-btn ${lang === "fr" ? "active" : ""}`} data-testid="program-lang-fr">FR</button>
                <button onClick={() => setLang("en")} className={`lang-btn ${lang === "en" ? "active" : ""}`} data-testid="program-lang-en">EN</button>
              </div>
            </div>
          </div>
        </div>
      </nav>
  );
}

function ProgramHero({ t }) {
  return (
      <section className="pt-32 print:pt-6 pb-16 px-6 md:px-12 print:px-0 bg-gradient-to-br from-[#0F1420] via-[#1B2A4E] to-[#0F1420]" data-testid="program-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 mb-4">
              <Droplets className="text-[#C5A059]" size={20} />
              <p className="text-[#C5A059] font-mono text-sm tracking-widest">{t.hero.label}</p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-[#F8FAFC] mb-4">
              {t.hero.title}
            </h1>
            <p className="text-lg md:text-xl text-[#C5A059] mb-6">{t.hero.subtitle}</p>
            <p className="text-base md:text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
              {t.hero.description}
            </p>
          </motion.div>
        </div>
      </section>
  );
}

function ContextSection({ t }) {
  return (
      <section className="section-padding" data-testid="program-context">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-6 section-title">
              {t.context.title}
            </h2>
            <p className="text-[#94A3B8] leading-relaxed max-w-4xl mb-12">{t.context.text}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.context.pillars.map((p, i) => {
                const Icon = iconMap[p.icon];
                return (
                  <div key={p.title} className="bg-[#161B28] border border-white/5 rounded-lg p-6 hover:border-[#C5A059]/30 transition-all" data-testid={`program-pillar-${i + 1}`}>
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 rounded-lg mb-4">
                      <Icon className="text-[#C5A059]" size={22} />
                    </div>
                    <h3 className="text-[#F8FAFC] font-serif font-semibold text-lg mb-2">{p.title}</h3>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function PhasesSection({ t }) {
  return (
      <section className="section-padding bg-[#0F1420]" data-testid="program-phases">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">{t.phases.label}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-12 section-title">
              {t.phases.title}
            </h2>
            <div className="space-y-0">
              {t.phases.items.map((phase, i) => (
                <motion.div
                  key={phase.period}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative flex gap-6 md:gap-10 pb-10 last:pb-0"
                  data-testid={`program-phase-${i + 1}`}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#C5A059]/10 border border-[#C5A059]/40 rounded-full text-[#C5A059] font-mono text-sm">
                      {i + 1}
                    </div>
                    {i < t.phases.items.length - 1 && <div className="w-px flex-1 bg-[#C5A059]/20 mt-2" />}
                  </div>
                  <div className="pb-2">
                    <p className="text-[#C5A059] font-mono text-sm tracking-wider mb-1">{phase.period}</p>
                    <h3 className="text-xl font-serif font-semibold text-[#F8FAFC] mb-2">{phase.title}</h3>
                    <p className="text-[#94A3B8] leading-relaxed max-w-3xl">{phase.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function GovernanceSection({ t }) {
  return (
      <section className="section-padding" data-testid="program-governance">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">{t.governance.label}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-6 section-title">
              {t.governance.title}
            </h2>
            <p className="text-lg text-[#C5A059] font-serif italic mb-12 max-w-3xl" data-testid="governance-principle">
              {t.governance.principle}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.governance.actors.map((actor, i) => {
                const Icon = iconMap[actor.icon];
                return (
                  <div key={actor.name} className="bg-[#161B28] border border-white/5 rounded-lg p-6" data-testid={`governance-actor-${i + 1}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#C5A059]/10 rounded-lg">
                        <Icon className="text-[#C5A059]" size={20} />
                      </div>
                      <h3 className="text-[#F8FAFC] font-semibold">{actor.name}</h3>
                    </div>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{actor.role}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function ImpactSection({ t }) {
  return (
      <section className="section-padding bg-[#0F1420]" data-testid="program-impact">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">{t.impact.label}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-12 section-title">
              {t.impact.title}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {t.impact.items.map((item, i) => (
                <div key={item} className="flex items-start gap-3 bg-[#161B28]/60 border border-white/5 rounded-lg p-5" data-testid={`program-impact-${i + 1}`}>
                  <Droplets className="text-[#C5A059] shrink-0 mt-0.5" size={18} />
                  <p className="text-[#F8FAFC] text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function CtaSection({ t }) {
  return (
      <section className="section-padding print:hidden" data-testid="program-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-4">{t.cta.title}</h2>
          <p className="text-[#94A3B8] mb-8">{t.cta.text}</p>
          <Link to="/" state={{ scrollTo: "contact" }} onClick={() => setTimeout(() => document.getElementById("contact")?.scrollIntoView(), 300)} className="btn-primary inline-block" data-testid="program-contact-btn">
            {t.cta.button}
          </Link>
        </div>
      </section>
  );
}

function ProgramFooter() {
  return (
      <footer className="footer py-12 px-6 md:px-12 print:hidden">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Skymoon" className="h-10 w-auto" />
            </Link>
            <p className="text-sm text-[#64748B]">
              © {new Date().getFullYear()} Skymoon Consultancy
            </p>
          </div>
        </div>
      </footer>
  );
}

export default function ProgramPage() {
  const [lang, setLang] = useState("fr");
  const t = programContent[lang];

  useEffect(() => {
    document.title = lang === "fr"
      ? "Résilience Hydrique Sénégal 2035 | Skymoon Consultancy"
      : "Senegal Water Resilience 2035 | Skymoon Consultancy";
  }, [lang]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#05080F] program-page">
      <div className="noise-overlay print:hidden" />
      <PrintHeader />
      <ProgramNav t={t} lang={lang} setLang={setLang} />
      <ProgramHero t={t} />
      <ContextSection t={t} />
      <PhasesSection t={t} />
      <GovernanceSection t={t} />
      <ImpactSection t={t} />
      <CtaSection t={t} />
      <ProgramFooter />
    </div>
  );
}
